import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { css } from '../components/UI';

// Reglas de utils/politicaPassword.js (backend), que es la que manda: aquí solo se
// adelantan las que se pueden revisar sin el servidor y se listan todas.
const MINIMO = 12;
const REGLAS = [
  `Al menos ${MINIMO} caracteres`,
  'Una mayúscula, una minúscula y un número',
  'Que no sea una palabra común ("selectshop", "password"…) ni tenga "1234" o "aaaa"',
  'Que no contenga tu nombre ni tu correo',
  'Que no sea ninguna de tus últimas 5 contraseñas',
];

export default function CambiarPassword() {
  const { user, logout, refreshUser } = useAuth();
  const [form, setForm] = useState({ passwordActual: '', passwordNueva: '', passwordConfirm: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.passwordNueva !== form.passwordConfirm) { setError('Las contraseñas nuevas no coinciden'); return; }
    const p = form.passwordNueva;
    if (p.length < MINIMO) { setError(`La nueva contraseña debe tener al menos ${MINIMO} caracteres`); return; }
    if (!/[A-ZÁÉÍÓÚÑÜ]/.test(p) || !/[a-záéíóúñü]/.test(p) || !/[0-9]/.test(p)) { setError('La nueva contraseña debe incluir una mayúscula, una minúscula y un número'); return; }
    if (form.passwordActual === form.passwordNueva) { setError('La nueva contraseña debe ser diferente a la actual'); return; }
    setLoading(true);
    try {
      await api.put('/auth/cambiar-password', { passwordActual: form.passwordActual, passwordNueva: form.passwordNueva });
      await refreshUser();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div style={{
      minHeight: '100vh', padding: 16,
      background: 'var(--chassis)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        width: 440, maxWidth: '100%',
        background: 'var(--panel)', borderRadius: 6, padding: 36,
        border: '1px solid var(--mesh)', boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 26 }}>
          <div aria-hidden="true" style={{ width: 48, height: 48, borderRadius: 4, border: '1px solid var(--plasma)', background: 'var(--well)', display: 'inline-grid', placeItems: 'center', marginBottom: 14, color: 'var(--plasma)' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
          </div>
          <h1 style={{ margin: 0, fontFamily: 'var(--label-font)', fontSize: 28, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.1, letterSpacing: '.05em', textTransform: 'uppercase' }}>Cambia tu contraseña</h1>
          <p style={{ margin: '8px 0 0', fontSize: 13.5, color: 'var(--ink-soft)', lineHeight: 1.5 }}>
            Hola <strong style={{ color: 'var(--ink)' }}>{user?.nombre}</strong>, establece una contraseña personal.
          </p>
        </div>

        <div role="note" style={{ background: 'var(--warn-bg)', border: '1px solid var(--warn-line)', borderRadius: 4, padding: '10px 14px', marginBottom: 22 }}>
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--warn)', lineHeight: 1.5 }}>
            {user?.motivoCambioPassword === 'politica'
              ? 'Actualizamos las reglas de seguridad y tu contraseña ya no las cumple. '
              : 'Tu contraseña fue asignada por el administrador. '}
            Para continuar, crea una nueva de <strong>al menos {MINIMO} caracteres</strong> que incluya <strong>una mayúscula, una minúscula y un número</strong>.
          </p>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div className="nx-label" style={{ marginBottom: 6 }}>Tu nueva contraseña debe tener</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: 'var(--ink-soft)', lineHeight: 1.6 }}>
            {REGLAS.map(r => <li key={r}>{r}</li>)}
          </ul>
          <div style={{ fontSize: 12, color: 'var(--glass)', marginTop: 6 }}>Tip: junta tres palabras que te sean fáciles de recordar, con mayúsculas y un par de números en medio. No uses fechas ni datos personales.</div>
        </div>

        <form onSubmit={submit}>
          {[
            { label: 'Contraseña actual', key: 'passwordActual', ph: user?.motivoCambioPassword === 'politica' ? 'La que usaste para entrar' : 'Contraseña asignada por el admin', auto: 'current-password' },
            { label: 'Nueva contraseña',  key: 'passwordNueva',  ph: `Mínimo ${MINIMO} caracteres`, auto: 'new-password' },
            { label: 'Confirmar contraseña', key: 'passwordConfirm', ph: 'Repite la nueva contraseña', auto: 'new-password' },
          ].map(({ label, key, ph, auto }) => (
            <div key={key} style={{ marginBottom: 16 }}>
              <label htmlFor={`pw-${key}`} className="nx-label" style={{ display: 'block', marginBottom: 6 }}>{label}</label>
              <input
                id={`pw-${key}`} type="password" required autoComplete={auto} value={form[key]}
                onChange={e => set(key, e.target.value)}
                placeholder={ph}
                style={{ ...css.input(key === 'passwordConfirm' && form.passwordConfirm && form.passwordNueva !== form.passwordConfirm), padding: '11px 14px', fontSize: 14 }}
              />
              {key === 'passwordConfirm' && form.passwordConfirm && form.passwordNueva !== form.passwordConfirm && (
                <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--danger)' }}>Las contraseñas no coinciden</p>
              )}
            </div>
          ))}

          {error && (
            <div role="alert" style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger-line)', color: 'var(--danger)', padding: '10px 14px', borderRadius: 4, fontSize: 13, marginBottom: 16 }}>
              {error}
            </div>
          )}

          <button type="submit" disabled={loading} className="nx-plate" style={{ marginBottom: 10, opacity: loading ? 0.6 : 1 }}>
            {loading ? 'Guardando…' : 'Guardar nueva contraseña'}
          </button>

          <button type="button" onClick={logout} className="nx-ghost" style={{ width: '100%', padding: '10px', fontSize: 13 }}>
            Cerrar sesión
          </button>
        </form>
      </div>
    </div>
  );
}
